import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { IncomingMessage, ServerResponse } from 'http';
import fetch from 'isomorphic-unfetch';
import { useMemo } from 'react';
import config from '.';

let globalApolloClient: ApolloClient<NormalizedCacheObject> | undefined;

export type ResolverContext = {
  req?: IncomingMessage;
  res?: ServerResponse;
};

/**
 * Creates and configures the ApolloClient
 * @param  {Object} [initialState={}]
 */
function createApolloClient(initialState = {}, cookie?: string) {
  const enhancedFetch = (
    input: RequestInfo | URL,
    init?: RequestInit | undefined,
  ) => {
    return fetch(input, {
      ...init,
      headers: {
        ...init?.headers,
        Cookie: cookie || '',
      },
    }).then(response => {
      return response;
    });
  };

  // Check out https://github.com/zeit/next.js/pull/4611 if you want to use the AWSAppSyncClient
  return new ApolloClient({
    ssrMode: typeof window === 'undefined', // Disables forceFetch on the server (so queries are only run once)
    link: new HttpLink({
      uri: `${config.api.airline}/graphql`, // Server URL (must be absolute)
      fetch: enhancedFetch,
      credentials: 'include',
    }),
    cache: new InMemoryCache({ addTypename: false }).restore(initialState),
  });
}

/**
 * Always creates a new apollo client on the server
 * Creates or reuses apollo client in the browser.
 * @param  {NormalizedCacheObject} initialState
 */
export function initializeApollo(
  initialState: any = null,
  context?: ResolverContext,
) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === 'undefined') {
    return createApolloClient(initialState, context?.req?.headers.cookie);
  }

  // Reuse client on the client-side
  if (!globalApolloClient) {
    globalApolloClient = createApolloClient(initialState, document.cookie);
  }

  return globalApolloClient;
}

export function useApollo(initialState?: any) {
  const store = useMemo(() => initializeApollo(initialState), [initialState]);
  return store;
}
